<template>
  <TransitionGroup
    tag="div"
    name="feed-anim"
    :class="['kds-quote-feed', { 'kds-quote-feed--paused': paused }]"
    lang="en"
    aria-live="polite"
    aria-atomic="false"
    aria-label="Debate feed — pricing panel"
  >
    <KdsQuoteCard
      v-for="(card, idx) in cards"
      :key="card.turnId"
      :class="{ 'kds-quote-feed__card--initial': card.isInitial }"
      :persona-name="card.personaName"
      :persona-id="card.personaId"
      :segment="card.segment"
      :quote="card.quote"
      :speaking="idx === 0"
    />
  </TransitionGroup>
</template>

<script setup lang="ts">
import KdsQuoteCard from '@/kds/components/KdsQuoteCard.vue';
import type { FeedCard } from '@/kds/components/KdsRoundTable.parts/types';

interface Props {
  cards: FeedCard[];
  paused?: boolean;
}

withDefaults(defineProps<Props>(), { paused: false });
</script>

<style scoped lang="scss">
.kds-quote-feed {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 320px;
  height: 480px;
  overflow: hidden;
  // Fade the bottom band instead of hard-clipping it: cards genuinely disappear
  // (alpha) rather than being covered by an opaque gradient painted on top.
  // Consequence: this mask creates a backdrop root, so cards inside cannot use
  // backdrop-filter — they get a translucent background instead.
  mask-image: linear-gradient(to bottom, black 0%, black 58%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 0%, black 58%, transparent 100%);
  pointer-events: none;
}

/* Feed enter/leave/move transitions — D5 */
.feed-anim-enter-active {
  animation: kds-settle 320ms var(--ease-settle) both;
}

/* Initial cards skip the enter animation */
.feed-anim-enter-active.kds-quote-feed__card--initial {
  animation: none;
  opacity: 1;
  transform: none;
}

.feed-anim-leave-active {
  transition: opacity 150ms var(--ease-out);
  position: absolute;
  width: 100%;
}

.feed-anim-leave-to {
  opacity: 0;
}

.feed-anim-move {
  transition: transform 200ms var(--ease-out);
}

/* Paused state: freeze card transitions to mirror KdsRoundTable visual pause */
.kds-quote-feed--paused {
  .feed-anim-enter-active,
  .feed-anim-leave-active,
  .feed-anim-move {
    animation: none !important;
    transition: none !important;
  }
}

/* Responsive: hidden on mobile/tablet (H7) */
@media (max-width: 959px) {
  .kds-quote-feed {
    display: none;
  }
}

/* Reduced motion: neutralise all feed-anim transitions (D7) */
@media (prefers-reduced-motion: reduce) {
  .feed-anim-enter-active,
  .feed-anim-leave-active,
  .feed-anim-move {
    animation: none !important;
    transition: none !important;
  }
}
</style>
